import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import User from '@/types/userType';
import fetchUser, { fetchUserByEmail } from '@/apis/fetchUser';
import emptyUser from '@/types/emptyUser';
import headerObject from '@/types/headerObj';
import emptyHeaderObj from '@/types/emptyHeaderObj';
import { getQueryClient } from './orgState';

interface UserState {
  user: User;
  isWithCorrectPermissions: boolean;
  lastUpdated: number;
  setUser: (Email: string) => Promise<User>;
  setPureUser: (user: User) => void;
  setUserOrg: (OrganizationUUID: string) => void;
  setUserByUUID: (UserUUID: string) => Promise<User>;
  setIsWithCorrectPermissions: (isWithCorrectPermissions: boolean) => void;
  clearUser: () => void;
}

const localHeaderObj: headerObject = emptyHeaderObj;

const useUserStore = create<UserState>()(
  persist(
    (set, get) => ({
      user: emptyUser,
      isWithCorrectPermissions: false,
      lastUpdated: Date.now(),
      setUser: async (Email: string) => {
        if (!Email) return get().user;

        localHeaderObj.Useremail = Email;

        // Get the queryClient
        const queryClient = getQueryClient();

        // First try to get the data from the cache
        const cachedData = queryClient.getQueryData<User>([
          'user',
          'byEmail',
          Email,
        ]);

        if (cachedData) {
          set({
            user: cachedData,
            lastUpdated: Date.now(),
          });
          return cachedData;
        }

        // If not in cache, fetch it and update the cache
        try {
          const data = await queryClient.fetchQuery({
            queryKey: ['user', 'byEmail', Email],
            queryFn: async () => {
              const user = await fetchUserByEmail(Email, localHeaderObj);
              return user || emptyUser;
            },
          });

          set({
            user: data,
            lastUpdated: Date.now(),
          });
          return data;
        } catch (error) {
          console.error('Error fetching user by email:', error);
          return get().user;
        }
      },

      setPureUser: (user: User) => {
        set({
          user: user,
        });

        // Invalidate relevant queries
        const queryClient = getQueryClient();
        if (user.UserUUID) {
          queryClient.invalidateQueries({
            queryKey: ['user', 'byUUID', user.UserUUID],
          });
        }
        if (user.Email) {
          queryClient.invalidateQueries({
            queryKey: ['user', 'byEmail', user.Email],
          });
        }
      },

      setUserByUUID: async (UserUUID: string) => {
        if (!UserUUID) return get().user;

        localHeaderObj.Useruuid = UserUUID;

        // Get the queryClient
        const queryClient = getQueryClient();

        // First try to get the data from the cache
        const cachedData = queryClient.getQueryData<User>([
          'user',
          'byUUID',
          UserUUID,
        ]);

        if (cachedData) {
          set({
            user: cachedData,
            lastUpdated: Date.now(),
          });
          return cachedData;
        }

        // If not in cache, fetch it and update the cache
        try {
          const data = await queryClient.fetchQuery({
            queryKey: ['user', 'byUUID', UserUUID],
            queryFn: async () => {
              const user = await fetchUser(UserUUID, localHeaderObj);
              return user || emptyUser;
            },
          });

          set({
            user: data,
            lastUpdated: Date.now(),
          });
          return data;
        } catch (error) {
          console.error('Error fetching user by UUID:', error);
          return get().user;
        }
      },

      setUserOrg: (OrganizationUUID: string) => {
        set({
          user: {
            ...get().user,
            OrganizationUUID: OrganizationUUID,
          },
          lastUpdated: Date.now(),
        });

        // Invalidate relevant queries
        const queryClient = getQueryClient();
        if (get().user.UserUUID) {
          queryClient.invalidateQueries({
            queryKey: ['user', 'byUUID', get().user.UserUUID],
          });
        }
        if (get().user.Email) {
          queryClient.invalidateQueries({
            queryKey: ['user', 'byEmail', get().user.Email],
          });
        }
      },

      setIsWithCorrectPermissions: (isWithCorrectPermissions: boolean) => {
        set({
          isWithCorrectPermissions: isWithCorrectPermissions,
          lastUpdated: Date.now(),
        });
      },
      clearUser: () => {
        set({
          user: emptyUser,
        });
      },
    }),
    {
      name: 'user-storage', // name of the item in the storage (must be unique)
      storage: createJSONStorage(() => localStorage), // (optional) by default, 'localStorage' is used
      // The user row is no longer persisted, only the client-only permission
      // flag. The credential moved to an httpOnly cookie, which is what lets
      // the server resolve identity itself (lib/server/dal.ts `getServerUser`),
      // so a localStorage copy is a cache for a fetch the server already did.
      // Keeping it was actively expensive: `user` was the one persisted value
      // that reaches rendered markup, so the server rendered `emptyUser` and the
      // client rehydrated a populated one, and `ssr: false` on 16 dashboard
      // pages existed to hide that mismatch. It also put the row's email and
      // name at rest in localStorage for no benefit.
      //
      // `useUpdatedData` still fetches the user client-side on every bootstrap
      // and `DashboardBootstrap` seeds it from the server, so nothing depended
      // on this surviving a reload.
      partialize: (state) => ({
        isWithCorrectPermissions: state.isWithCorrectPermissions,
        lastUpdated: state.lastUpdated,
      }),
      onRehydrateStorage: (state) => {
        const STORAGE_CLEAR_DELAY = 1000 * 60 * 60 * 1; // 1 hour in milliseconds
        const STORAGE_KEY = 'user-storage';

        const clearStorage = () => {
          localStorage.removeItem(STORAGE_KEY);
        };

        return (rehydratedState, error) => {
          if (error) {
            console.error('Error rehydrating state:', error);
          } else {
            const currentTime = Date.now();
            const storedTime = rehydratedState?.lastUpdated || 0;

            if (currentTime - storedTime > STORAGE_CLEAR_DELAY) {
              rehydratedState!.isWithCorrectPermissions = false;
            } else {
              setTimeout(
                clearStorage,
                STORAGE_CLEAR_DELAY - (currentTime - storedTime)
              );
            }
          }
        };
      },
    }
  )
);

export default useUserStore;
